import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'components/ui/sheet';
import { Button } from 'components/ui/button';
import { OrganizationData } from '../../services/organization-service';
import ConfirmationModal from '../../../../components/blocks/confirmation-modal/confirmation-modal';
import { Building2, Calendar, Globe, Mail, Phone } from 'lucide-react';
import { Separator } from 'components/ui/separator';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'components/ui/dialog';
import { EditOrganizationDetails } from '../edit-organization-details/edit-organization-details';

interface OrganizationDetailsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organization: OrganizationData;
}

export function OrganizationDetails({ open, onOpenChange, organization }: Readonly<OrganizationDetailsSheetProps>) {
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { t } = useTranslation();

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
  };

  /* const handleDeactivate = () => {
    setIsDeactivateModalOpen(true);
  }; */

  const handleConfirmDeactivation = async () => {
    try {
      // TODO: Implement deactivation logic
      //console.log('Deactivating organization:', organization);
      setIsDeactivateModalOpen(false);
    } catch (error) {
      console.error('Failed to deactivate organization:', error);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
        <SheetContent className="flex flex-col h-screen sm:h-[calc(100dvh-48px)] justify-between w-full sm:min-w-[450px] md:min-w-[450px] lg:min-w-[450px] sm:fixed sm:top-[57px]">
          <div className="flex flex-col">
            <SheetHeader className="hidden">
              <SheetTitle />
              <SheetDescription />
            </SheetHeader>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-4">
                <div className="relative overflow-hidden rounded-full border shadow-sm border-white h-16 w-16 flex items-center justify-center bg-primary/10">
                  <Building2 className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">
                    {organization.name}
                  </h2>
                  <div className="flex items-center space-x-2 mt-1">
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${organization.isActive ? 'bg-success' : 'bg-error'}`}
                    />
                    <span
                      className={`text-sm ${organization.isActive ? 'text-success' : 'text-error'}`}
                    >
                      {organization.isActive ? t('ACTIVE') : t('INACTIVE')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <Separator className="mb-6" />
            <div className="space-y-6">
              {/*  <div className="flex items-center space-x-4">
                <div className="text-base font-thin text-medium-emphasis w-24">{t('CODE')}</div>
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-high-emphasis" />
                  <div className="text-base font-normal text-high-emphasis">
                    {organization.code}
                  </div>
                </div>
              </div> */}

              <div className="flex items-center space-x-4">
                <div className="text-base font-thin text-medium-emphasis w-24">{t('ORGANIZATION')}</div>
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-high-emphasis" />
                  <div className="text-base font-normal text-high-emphasis">
                    {(!organization.name || organization.name === '') ? t('NOT_PROVIDED') : organization.name}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="text-base font-thin text-medium-emphasis w-24">{t('PHONE')}</div>
                <div className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-high-emphasis" />
                  <div className="text-base font-normal text-high-emphasis">
                    {(!organization.phoneNumber || organization.phoneNumber === '') ? t('NOT_PROVIDED') : organization.phoneNumber}
                  </div>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="text-base font-thin text-medium-emphasis w-24">{t('EMAIL')}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-high-emphasis shrink-0" />
                    <div className="text-base font-normal text-high-emphasis break-words">
                      {organization.email}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="text-base font-thin text-medium-emphasis w-24">{t('WEBSITE')}</div>
                <div className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-high-emphasis" />
                  <div className="text-base font-normal text-high-emphasis">
                    {organization.websiteUrl ? (
                      <a
                        href={organization.websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {organization.websiteUrl}
                      </a>
                    ) : (
                      t('NOT_PROVIDED')
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="text-base font-thin text-medium-emphasis w-24">{t('CREATED_ON')}</div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-high-emphasis" />
                  <div className="text-base font-normal text-high-emphasis">
                    {new Date(organization.createdDate).toLocaleString('de-CH', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })}
                  </div>
                </div>
              </div>



              {organization.address && (
                <div className="flex items-start space-x-4">
                  <div className="text-base font-thin text-medium-emphasis w-24">{t('ADDRESS')}</div>
                  <div className="text-base font-normal text-high-emphasis">
                    {organization.address}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex w-full flex-col gap-2">
            <Button size="default" className="w-full h-9" onClick={handleEdit}>
              {t('EDIT')}
            </Button>

            {/* {organization.isActive && (
              <Button
                variant="outline"
                className="w-full text-error hover:text-error hover:opacity-50"
                onClick={handleDeactivate}
              >
                {t('DEACTIVATE_ORGANIZATION')}
              </Button>
            )} */}
          </div>
        </SheetContent>
      </Sheet>

      <ConfirmationModal
        open={isDeactivateModalOpen}
        onOpenChange={setIsDeactivateModalOpen}
        title={t('DEACTIVATE_ORGANIZATION')}
        description={t('DEACTIVATE_ORGANIZATION_CONFIRMATION')}
        onConfirm={handleConfirmDeactivation}
        confirmText={t('CONFIRM')}
        cancelText={t('CANCEL')}
      />

      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        {isEditModalOpen && (
          <EditOrganizationDetails
            organization={organization}
            onClose={handleEditModalClose}
          />
        )}
      </Dialog>
    </>
  );
} 